/** Pairs: [livingsHost, condoHost]. Livings whitelist = first column. */
export const BRANDED_PLATFORM_DOMAIN_PAIRS = [
    ['livings.local.condo', 'investor-portal.local.condo'],
    ['livings.dev.condogroup.tech', 'investor-portal.dev.condogroup.tech'],
    ['livings.stg.condogroup.tech', 'investor-portal.stg.condogroup.tech'],
    ['livings.de', 'verwaltung.condogroup.de'],
] as const;

export type BrandedPlatformDomainPair = (typeof BRANDED_PLATFORM_DOMAIN_PAIRS)[number];

export const LIVINGS_HOSTNAMES: readonly string[] = BRANDED_PLATFORM_DOMAIN_PAIRS.map(([livingsHost]) => livingsHost);

/** Exact host or subdomain of `baseHostname` (e.g. www.verwaltung.condogroup.de). */
const matchesHostnameOrSubdomain = (hostname: string, baseHostname: string): boolean => {
    return hostname === baseHostname || hostname.endsWith(`.${baseHostname}`);
};

export const isLivingsHostname = (hostname: string): boolean => {
    const normalized = hostname.toLowerCase();
    return LIVINGS_HOSTNAMES.some(livingsHostname => matchesHostnameOrSubdomain(normalized, livingsHostname));
};

const protocolForHostname = (hostname: string): 'http' | 'https' => {
    return hostname.endsWith('.local.condo') ? 'http' : 'https';
};

/**
 * When origin is a known Condo brand host (or subdomain), return the opposite Livings platform base URL.
 * Returns null when hostname is unknown / not in the map.
 */
export const resolveLivingsRedirectUrl = (originHostname: string): string | null => {
    const normalized = originHostname.toLowerCase();
    const pair = BRANDED_PLATFORM_DOMAIN_PAIRS.find(([, condoHost]) => matchesHostnameOrSubdomain(normalized, condoHost));
    if (!pair) {
        return null;
    }
    const [livingsHost] = pair;
    return `${protocolForHostname(livingsHost)}://${livingsHost}`;
};

/**
 * When origin is a known Livings brand host (or subdomain), return the opposite Condo platform base URL.
 * Returns null when hostname is unknown / not in the map.
 */
export const resolveCondoRedirectUrl = (originHostname: string): string | null => {
    const normalized = originHostname.toLowerCase();
    const pair = BRANDED_PLATFORM_DOMAIN_PAIRS.find(([livingsHost]) => matchesHostnameOrSubdomain(normalized, livingsHost));
    if (!pair) {
        return null;
    }
    const [, condoHost] = pair;
    return `${protocolForHostname(condoHost)}://${condoHost}`;
};
